/**
 * Free-month pop-up on the demo (FM-16): helpers only, no I/O.
 *
 * Hand-over contract for FM-13 (PROD /start): the demo navigates the same tab
 * to `<REACT_APP_FREE_MONTH_URL>?lng=<nl|en>#name=…&email=…&org=…`. PROD
 * /start reads `name`, `email` and `org` with
 * `new URLSearchParams(location.hash.slice(1))`, then clears the fragment with
 * `history.replaceState`. Only the language goes in the query string; the
 * fields never do (a fragment is not sent to any server).
 */

export const FREE_MONTH_LOCALES = ['nl', 'en'] as const;
export type FreeMonthLocale = (typeof FREE_MONTH_LOCALES)[number];

export const FREE_MONTH_MAX = { name: 200, email: 254, organisation: 200 } as const;

export type FreeMonthFields = {
  name: string;
  email: string;
  organisation: string;
};

const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

/**
 * The PROD /start URL from the build-time `REACT_APP_FREE_MONTH_URL`, or null
 * (feature off). Read at call time. https only (http for localhost), no
 * credentials, no query, no fragment.
 */
export const freeMonthStartUrl = (): string | null => {
  const raw = (process.env.REACT_APP_FREE_MONTH_URL ?? '').trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const secure =
    url.protocol === 'https:' ||
    (url.protocol === 'http:' && LOCAL_HOSTS.includes(url.hostname));
  if (!secure) return null;
  if (url.username || url.password || url.search || url.hash) return null;
  return url.origin + url.pathname;
};

/** `nl-NL` -> `nl`, `en-GB` -> `en`; every other language (de, es, …) -> null. */
export const freeMonthLocale = (language?: string | null): FreeMonthLocale | null => {
  const base = (language || '').toLowerCase().split(/[-_]/)[0];
  return (FREE_MONTH_LOCALES as readonly string[]).includes(base)
    ? (base as FreeMonthLocale)
    : null;
};

/** Fields go after `#` only; see the contract above. */
export const buildFreeMonthHandoffUrl = (
  base: string,
  fields: FreeMonthFields,
  locale: FreeMonthLocale
): string => {
  const cut = (value: string, max: number) => value.trim().slice(0, max);
  const hash = new URLSearchParams({
    name: cut(fields.name, FREE_MONTH_MAX.name),
    email: cut(fields.email, FREE_MONTH_MAX.email),
    org: cut(fields.organisation, FREE_MONTH_MAX.organisation),
  }).toString();
  return `${base}?lng=${locale}#${hash}`;
};

/** Light check only (PROD does the real work-mail check): one @, a dot in the domain, no spaces. */
export const looksLikeEmail = (value: string): boolean => {
  const parts = value.trim().split('@');
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  return (
    !!local &&
    !/\s/.test(value.trim()) &&
    domain.includes('.') &&
    !domain.startsWith('.') &&
    !domain.endsWith('.')
  );
};
